/** Collapse whitespace accidentally injected into a URL during SSE token reassembly. */
export const collapseUrlSpaces = (url) => url.replace(/\s+/g, "");

/**
 * Fix spaces inside markdown link destinations only: `[label](https:// broken url)`.
 * Stops at the closing `)` so trailing prose is never absorbed into the href.
 */
export const repairSpacesInMarkdownLinkUrls = (text) =>
  text.replace(
    /\]\((https?:\/\/[^)\s]*(?:\s+[^)\s]+)+)\)/g,
    (_, url) => `](${collapseUrlSpaces(url)})`
  );

/** True when a spaced token is likely a split URL fragment, not trailing prose. */
const isSseUrlContinuationToken = (token) => {
  if (!token) return false;
  const core = token.replace(/[.,!?;:]+$/, "");
  if (!core) return false;
  if (/[0-9]/.test(core)) return true;
  if (/[/?#&=%._~:+-]/.test(core)) return true;
  if (/^[A-Z0-9._-]{2,}$/.test(core)) return true;
  return false;
};

/**
 * Fix spaces inside bare/autolink URLs in prose (not markdown `](url)` destinations).
 * Only merges tokens that look like split URL fragments (digits, query chars, etc.).
 */
export const repairSpacesInBareUrls = (text) => {
  const re = /(?<!\]\()https?:\/\//g;
  let result = "";
  let lastIndex = 0;
  let match = re.exec(text);

  while (match) {
    result += text.slice(lastIndex, match.index);
    const start = match.index;
    let end = start + match[0].length;

    while (end < text.length && !/\s/.test(text[end]) && !/[<>"')\]]/.test(text[end])) {
      end += 1;
    }

    while (end < text.length) {
      const tail = text.slice(end);
      const spaced = tail.match(/^\s+([^\s<>"')\]]+)/);
      if (!spaced || !isSseUrlContinuationToken(spaced[1])) break;
      end += spaced[0].length;
    }

    result += collapseUrlSpaces(text.slice(start, end));
    lastIndex = end;
    re.lastIndex = end;
    match = re.exec(text);
  }

  result += text.slice(lastIndex);
  return result;
};

/** Repair SSE-broken URLs in assistant markdown without corrupting link boundaries. */
export const repairSpacesInsideUrls = (text) =>
  repairSpacesInMarkdownLinkUrls(repairSpacesInBareUrls(text));
